import React from "react";
import { motion } from "framer-motion";
import { Heart, Compass, Sparkles, ShieldCheck, ArrowRight, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import PageHero from "@/components/site/PageHero";

const values = [
  {
    id: "relationships",
    icon: Heart,
    title: "Relationships first",
    desc: "Every load we broker is a small relationship between a shipper, a driver and us. We treat each one like it matters — because it does.",
  },
  {
    id: "transparency",
    icon: Compass,
    title: "Transparent always",
    desc: "Straight rates, straight timelines, straight communication. If something changes, you'll hear it from us first.",
  },
  {
    id: "craft",
    icon: Sparkles,
    title: "Craft over scale",
    desc: "We'd rather move 100 loads beautifully than 1,000 loads badly. Quality of service is the only KPI we obsess over.",
  },
  {
    id: "compliance",
    icon: ShieldCheck,
    title: "Non-negotiable safety",
    desc: "A compliance and safety standard our shippers — and the public — can rely on. Always.",
  },
];

const timeline = [
  { year: "2010", title: "Founded in Gauteng", desc: "Started as two operators with a phone and a fax machine." },
  { year: "2014", title: "Network crosses 200 carriers", desc: "Cross-border SADC services launched into Botswana & Namibia." },
  { year: "2018", title: "Specialised division", desc: "Abnormal, heavy-haul and cold-chain teams stood up." },
  { year: "2023", title: "1,000+ carrier network", desc: "Recognised across mining, FMCG, manufacturing and agriculture." },
  { year: "Today", title: "Trusted brokerage", desc: "Moving thousands of loads every month — relationship by relationship." },
];

const reach = [
  "South Africa", "Botswana", "Namibia", "Zambia", "Zimbabwe", "Mozambique", "Lesotho", "Eswatini",
];

export default function About() {
  return (
    <>
      <PageHero
        eyebrow="About FreightXchange"
        badge={<><MapPin className="h-3.5 w-3.5" /> Gauteng · Southern Africa</>}
        title="Built on relationships,"
        accent="moved by trucks."
        description="FreightXchange is an independent freight brokerage with one job: matching the right load to the right carrier — every time. We don't own trucks. We own the relationships."
        primaryCta={{ label: "Get in touch", to: "/contact", testId: "about-cta-contact" }}
        image="https://images.unsplash.com/photo-1592838064575-70ed626d3a0e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
      />

      {/* Mission */}
      <section className="relative py-20 md:py-28 bg-[#F8FAFC] border-y border-[#E2E8F0]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs md:text-sm uppercase tracking-[0.24em] font-bold text-[#0070C0]">
              Our story
            </div>
            <h2 className="font-display mt-4 text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tighter text-[#002050] leading-[1.05]">
              A brokerage that runs on trust — not portals.
            </h2>
          </div>
          <div className="lg:col-span-7 text-lg text-[#1F2937]/80 leading-relaxed space-y-5">
            <p>
              FreightXchange was born from a frustration our founders kept
              hearing on both sides of the freight chain. Shippers were tired
              of chasing carriers; carriers were tired of chasing loads. The
              middle was messy, manual and full of surprises.
            </p>
            <p>
              We built FreightXchange as the trusted middle. One phone call to
              us, and a vetted truck rolls — whether you're moving pallets
              into Cape Town, ore out of Mpumalanga, or a project rig into
              Maputo.
            </p>
            <blockquote className="mt-2 border-l-4 border-[#00AEEF] pl-5 italic text-base text-[#1F2937]/75">
              “Vehicles are one of the best modes of transportation.
              Relationships are one of the best vehicles of transformation.”
              <span className="block mt-2 not-italic text-sm font-semibold text-[#0070C0]">
                — Kate McGahan
              </span>
            </blockquote>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="relative py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="max-w-3xl">
            <div className="text-xs md:text-sm uppercase tracking-[0.24em] font-bold text-[#0070C0]">
              What we believe
            </div>
            <h2 className="font-display mt-4 text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tighter text-[#002050] leading-[1.05]">
              Four values that show up on every load.
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <motion.div
                  key={v.id}
                  data-testid={`about-value-${v.id}`}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.45, delay: (i % 2) * 0.08 }}
                  className="bg-white rounded-2xl border border-[#E2E8F0] p-8 hover:border-[#0070C0]/40 hover:shadow-[0_20px_50px_-25px_rgba(0,32,80,0.25)] transition-all flex gap-5"
                >
                  <div className="h-12 w-12 rounded-xl bg-[#0070C0]/10 flex items-center justify-center shrink-0">
                    <Icon className="h-6 w-6 text-[#0070C0]" strokeWidth={1.75} />
                  </div>
                  <div>
                    <h3 className="font-display text-xl md:text-2xl font-bold text-[#002050] tracking-tight">
                      {v.title}
                    </h3>
                    <p className="mt-2 text-[15px] text-[#1F2937]/75 leading-relaxed">
                      {v.desc}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="relative py-20 md:py-28 bg-[#F8FAFC] border-y border-[#E2E8F0]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.24em] font-bold text-[#0070C0]">
              How we got here
            </div>
            <h2 className="font-display mt-4 text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tighter text-[#002050] leading-[1.05]">
              A decade of loads — and counting.
            </h2>
          </div>

          <div className="mt-14 relative">
            <div className="absolute top-0 bottom-0 left-4 md:left-1/2 md:-translate-x-1/2 w-px bg-[#E2E8F0]" />
            <div className="space-y-10">
              {timeline.map((t, i) => (
                <motion.div
                  key={t.year}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  className={`relative grid md:grid-cols-2 gap-6 md:gap-12 ${
                    i % 2 === 0 ? "" : "md:[&>*:first-child]:order-2"
                  }`}
                >
                  <div className={`pl-12 md:pl-0 ${i % 2 === 0 ? "md:text-right md:pr-12" : "md:pl-12"}`}>
                    <div className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter text-[#0070C0]">
                      {t.year}
                    </div>
                    <div className="mt-2 font-display text-xl md:text-2xl font-bold text-[#002050]">
                      {t.title}
                    </div>
                    <p className="mt-2 text-[15px] text-[#1F2937]/75 leading-relaxed">
                      {t.desc}
                    </p>
                  </div>
                  <div />
                  <div className="absolute left-4 md:left-1/2 md:-translate-x-1/2 -translate-y-1 h-4 w-4 rounded-full bg-[#0070C0] border-4 border-white shadow-[0_0_0_2px_rgba(0,112,192,0.25)]" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Reach */}
      <section className="relative py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="rounded-3xl bg-[#002050] p-10 md:p-16 relative overflow-hidden">
            <div className="absolute inset-0 bg-grid-dark opacity-50" />
            <div className="absolute -top-24 -right-24 h-[420px] w-[420px] rounded-full bg-[#00AEEF]/25 blur-3xl" />

            <div className="relative grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-6">
                <div className="text-xs uppercase tracking-[0.24em] font-bold text-[#00AEEF]">
                  Where we move
                </div>
                <h3 className="font-display mt-3 text-3xl md:text-4xl font-extrabold tracking-tighter text-white leading-[1.05]">
                  Across 8 SADC countries — and growing.
                </h3>
                <p className="mt-4 text-white/80 leading-relaxed">
                  Cross-border documentation, border-post coordination and
                  in-country compliance — all part of the service.
                </p>
              </div>
              <div className="lg:col-span-6 flex flex-wrap gap-2">
                {reach.map((r) => (
                  <span
                    key={r}
                    className="inline-flex items-center gap-1.5 bg-white/5 border border-white/15 text-white/90 px-3.5 py-2 rounded-full text-sm font-semibold backdrop-blur"
                  >
                    <MapPin className="h-3.5 w-3.5 text-[#00AEEF]" /> {r}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-20 md:pb-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="rounded-3xl border border-[#E2E8F0] bg-white p-10 md:p-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-[0_30px_80px_-30px_rgba(0,32,80,0.18)]">
            <div>
              <div className="text-xs uppercase tracking-[0.24em] font-bold text-[#0070C0]">
                Ready to work together?
              </div>
              <h3 className="font-display mt-2 text-2xl md:text-3xl font-bold text-[#002050]">
                Send us a load — or a CV.
              </h3>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-[#0070C0] hover:bg-[#002050] text-white px-7 py-3.5 rounded-xl font-bold text-sm transition-colors group"
            >
              Get in touch
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
